import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaReact, FaJs, FaHtml5, FaCss3Alt } from 'react-icons/fa';
import { SiTypescript, SiTailwindcss, SiRedux, SiNextdotjs, SiVuedotjs } from 'react-icons/si';
import './Projects.css';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'web', label: 'Web Apps' },
    { id: 'mobile', label: 'Mobile Apps' },
    { id: 'frontend', label: 'Frontend' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Secure File Storage System',
      description: 'This is the frontend of the Secure File Storage System — a lightweight file management app that lets users securely upload, view, download, and delete their files. It integrates with a backend service (Node.js + MongoDB) and optionally uses AWS S3 for cloud storage.',
      image: '🎨', // You can replace this with actual image: import('./image/project1.jpg')
      category: 'frontend',
      technologies: ['Next.js', 'TypeScript', 'tailwind CSS',  'AWS S3', 'MongoDB', 'Node.js'],
      icons: [FaReact, FaJs, FaCss3Alt],
      github: 'https://github.com/Divyanshi1598/Frontend-Secure-File-Storage-System.git',
      live: 'http://3.95.29.135:3000/',
      featured: true
    },
    {
      id: 2,
      title: 'Restorant Website',
      description: ' project description for the Restorant Website, which showcases a modern restaurant interface with menu items and reservation features.',
      image: '📋', // You can replace this with actual image: import('./image/project2.jpg')
      category: 'web',
      technologies: ['Angular','TypeScript', 'Boostrap', 'Nodejs', 'MongoDB'],
      icons: [FaJs, FaHtml5, FaCss3Alt],
      github: '',
      live: 'https://tranquil-frangollo-3f630c.netlify.app/login',
      featured: true
    },
    {
      id: 3,
      title: 'E-Commerce Website',
      description: 'A web application that integrates with external APIs to fetch and display dynamic data.',
      image: '🌐', // You can replace this with actual image: import('./image/project3.jpg')
      category: 'web',
      technologies: ['Reactjs','JavaScript', 'bootstrap', 'CSS3' ,"nodejs ","MongoDB"],
      icons: [FaJs, FaHtml5, FaCss3Alt],
      github: '',
      live: 'https://beamish-trifle-99181f.netlify.app/login',
      featured: false
    },
    {
      id: 4,
      title: 'Admin Dashboard',
      description: 'A responsive admin dashboard template with charts and data visualization.',
      image: '📄', // You can replace this with actual image: import('./image/project4.jpg')
      category: 'frontend',
      technologies: ['Reactjs','JavaScript', 'bootstrap', 'CSS3', "chartjs"],
      icons: [FaHtml5, FaCss3Alt, FaJs],
      github: '',
      live: 'https://quiet-bienenstitch-5751ca.netlify.app/',
      featured: false
    },
    {
      id: 5,
      title: 'Quiz Application',
      description: 'An interactive quiz application with multiple-choice questions and score tracking.',
      image: '❓', // You can replace this with actual image: import('./image/project5.jpg')
      category: 'web',
      technologies: ['JavaScript', 'HTML5', 'CSS3', 'Interactive UI'],
      icons: [FaJs, FaHtml5, FaCss3Alt],
      github: 'https://github.com/Divyanshi1598/Quiz-App',
      live: 'https://divyanshi1598.github.io/Quiz-App/',
      featured: false
    },
    {
      id: 6,
      title: 'Travel website',
      description: 'A travel website that showcases popular destinations and travel tips.',
      image: '⚛️', // You can replace this with actual image: import('./image/project6.jpg')
      category: 'frontend',
      technologies: ['Angular', 'typescript', 'bootstrap', 'CSS3'],
      icons: [FaReact, FaJs],
      github: '',
      live: 'https://playful-rugelach-931dc8.netlify.app/',
      featured: false
    },

    {
      id: 7,
      title: 'ToDo List Application',
      description: 'A simple ToDo list application that allows users to add, edit, and delete tasks.',
      image: '⚛️', // You can replace this with actual image: import('./image/project6.jpg')
      category: 'frontend',
      technologies: ['HIML5', 'Javascript', 'CSS3'],
      icons: [FaReact, FaJs],
      github: '',
      live: 'https://divyanshi1598.github.io/todo_app1/',
      featured: false
    }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  // Function to render project image or emoji
  const renderProjectImage = (image) => {
    if (typeof image === 'string' && image.startsWith('http')) {
      return <img src={image} alt="Project" className="project-image-img" />;
    } else if (typeof image === 'string' && image.length <= 2) {
      // Emoji
      return <div className="project-emoji">{image}</div>;
    } else {
      // Imported image
      return <img src={image} alt="Project" className="project-image-img" />;
    }
  };

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          My Projects
        </motion.h2>
        
        <motion.p
          className="section-subtitle"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Here are some of my recent frontend projects that showcase my skills and experience
        </motion.p>

        <motion.div
          className="project-filters"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {filters.map((filter) => (
            <button
              key={filter.id}
              className={`filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </motion.div>

        <motion.div
          className="projects-grid"
          layout
        >
          <AnimatePresence mode="wait">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className={`project-card ${project.featured ? 'featured' : ''}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                layout
              >
                <div className="project-image">
                  {renderProjectImage(project.image)}
                  {project.featured && (
                    <div className="featured-badge">Featured</div>
                  )}
                </div>
                
                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  
                  <div className="project-technologies">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                  
                  <div className="project-icons">
                    {project.icons.map((Icon, iconIndex) => (
                      <Icon key={iconIndex} className="tech-icon" />
                    ))}
                  </div>
                  
                  <div className="project-links">
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      <FaGithub /> Code
                    </a>
                    <a
                      href={project.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      <FaExternalLinkAlt /> View Project
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <motion.div
          className="projects-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <p>Interested in working together?</p>
          <a href="#contact" className="btn btn-primary">
            Let's Talk
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 